import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, Plus } from "lucide-react";
import { actions, evaluateAwardControls, listRequisitions, listRfqs, listVendors, rfqDetail } from "@/lib/api";
import type { AppRole, Rfq } from "@/lib/types";
import { displayDate, money } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { QuotationEvidenceDialog } from "@/quotation-evidence";
import { provenanceBadges } from "@/lib/quotation-integrity.mjs";
import { useData, matches, reportActionError, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { AppLink } from "@/components/app-link";
import { Panel } from "@/components/panel";
import { ControlPassportPanel } from "@/components/control-passport-panel";

export function Rfqs({
  organisation,
  refresh,
  query,
  recordId,
  can,
  changed,
  role,
  navigate,
}: ViewProps & {
  recordId: string;
  role: AppRole;
  navigate: (view: string, recordId?: string) => void;
}) {
  const { data, error, loading } = useData(
    () => listRfqs(organisation.id),
    [organisation.id, refresh],
  );
  const [creating, setCreating] = useState(false);
  const [quoteRfq, setQuoteRfq] = useState<string>();
  const [evaluateRfq, setEvaluateRfq] = useState<string>();
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.filter((row) =>
    matches(
      query,
      row.id,
      row.rfq_no,
      row.title,
      row.status,
      row.requisitions?.request_no,
    ),
  );
  async function act(id: string, action: "publish" | "close") {
    setBusy(id);
    try {
      if (action === "publish") await actions.publishRfq(organisation.id, id);
      else await actions.closeRfq(organisation.id, id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }
  if (recordId) {
    const rfq = data.find((row) => row.id === recordId);
    if (!rfq)
      return (
        <PageHeader
          eyebrow="SOURCE EVENT"
          title="RFQ unavailable"
          description="This sourcing record is not available in the current organisation."
        >
          <Button variant="outline" asChild>
            <AppLink view="rfqs" navigate={navigate}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              All RFQs
            </AppLink>
          </Button>
        </PageHeader>
      );
    return (
      <>
        <RfqRecord
          row={rfq}
          organisationId={organisation.id}
          busy={busy}
          can={can}
          role={role}
          navigate={navigate}
          onAct={act}
          onQuote={setQuoteRfq}
          onEvaluate={setEvaluateRfq}
        />
        <QuotationEvidenceDialog
          rfqId={quoteRfq}
          onOpenChange={(open) => !open && setQuoteRfq(undefined)}
          organisationId={organisation.id}
          canCapture={can("procure")}
          canVerify={role === "procurement" || role === "approver"}
          onDone={changed}
        />
        <EvaluationDialog
          rfqId={evaluateRfq}
          onOpenChange={(open) => !open && setEvaluateRfq(undefined)}
          organisationId={organisation.id}
          onDone={changed}
          navigate={navigate}
        />
      </>
    );
  }
  return (
    <>
      <PageHeader
        eyebrow="SOURCE"
        title="RFQs & quotations"
        description="Invite suppliers, compare offers and award with context."
      >
        {can("procure") && (
          <Button className="min-h-11" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Create RFQ
          </Button>
        )}
      </PageHeader>
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={["RFQ", "Source request", "Deadline", "Status", "Action"]}
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <AppLink
                      view="rfqs"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open ${row.rfq_no}: ${row.title}`}
                      className="inline-block"
                    >
                      <strong>{row.rfq_no}</strong>
                      <small className="block text-[var(--text-secondary)]">{row.title}</small>
                    </AppLink>
                  </td>
                  <td>
                    <AppLink
                      view="requisitions"
                      recordId={row.requisition_id}
                      navigate={navigate}
                    >
                      {row.requisitions?.request_no}
                    </AppLink>
                    <small className="block text-[var(--text-secondary)]">
                      {money(row.requisitions?.estimated_value)}
                    </small>
                  </td>
                  <td>{displayDate(row.deadline)}</td>
                  <td>
                    <Badge value={row.status} />
                  </td>
                  <td>
                    <RfqActions
                      row={row}
                      busy={busy}
                      can={can}
                      role={role}
                      onAct={act}
                      onQuote={setQuoteRfq}
                      onEvaluate={setEvaluateRfq}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Sourcing events">
            {rows.map((row) => (
              <article
                key={row.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <AppLink
                      view="rfqs"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open ${row.rfq_no}: ${row.title}`}
                      className="block"
                    >
                      <span className="block text-xs font-bold tracking-[.08em] text-[var(--text-muted)]">
                        {row.rfq_no}
                      </span>
                      <span className="mt-1 block text-base font-semibold">{row.title}</span>
                    </AppLink>
                  </div>
                  <Badge value={row.status} />
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Source request</dt>
                    <dd className="mt-0.5 font-medium">
                      <AppLink
                        view="requisitions"
                        recordId={row.requisition_id}
                        navigate={navigate}
                      >
                        {row.requisitions?.request_no}
                      </AppLink>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Deadline</dt>
                    <dd className="mt-0.5">{displayDate(row.deadline)}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-xs text-[var(--text-muted)]">Approved estimate</dt>
                    <dd className="mt-0.5 font-semibold tabular-nums">
                      {money(row.requisitions?.estimated_value)}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-[var(--border)] pt-4">
                  <RfqActions
                    row={row}
                    busy={busy}
                    can={can}
                    role={role}
                    onAct={act}
                    onQuote={setQuoteRfq}
                    onEvaluate={setEvaluateRfq}
                    mobile
                  />
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <Empty
          title="No sourcing events yet"
          description="Create an RFQ after a requisition receives approval."
          action={
            can("procure") && (
              <Button onClick={() => setCreating(true)}>Create RFQ</Button>
            )
          }
        />
      )}
      <RfqDialog
        open={creating}
        onOpenChange={setCreating}
        organisationId={organisation.id}
        onDone={changed}
      />
      <QuotationEvidenceDialog
        rfqId={quoteRfq}
        onOpenChange={(open) => !open && setQuoteRfq(undefined)}
        organisationId={organisation.id}
        canCapture={can("procure")}
        canVerify={role === "procurement" || role === "approver"}
        onDone={changed}
      />
      <EvaluationDialog
        rfqId={evaluateRfq}
        onOpenChange={(open) => !open && setEvaluateRfq(undefined)}
        organisationId={organisation.id}
        onDone={changed}
        navigate={navigate}
      />
    </>
  );
}

function RfqRecord({
  row,
  organisationId,
  busy,
  can,
  role,
  navigate,
  onAct,
  onQuote,
  onEvaluate,
}: {
  row: Rfq;
  organisationId: string;
  busy: string;
  can: ViewProps["can"];
  role: AppRole;
  navigate: (view: string, recordId?: string) => void;
  onAct: (id: string, action: "publish" | "close") => void;
  onQuote: (id: string) => void;
  onEvaluate: (id: string) => void;
}) {
  const { data, error, loading } = useData(
    () => rfqDetail(organisationId, row.id),
    [organisationId, row.id],
  );
  return (
    <>
      <PageHeader
        eyebrow="SOURCE EVENT"
        title={`${row.rfq_no} · ${row.title}`}
        description="Sourcing scope, invited suppliers and current quotation evidence."
      >
        <Button variant="outline" asChild>
          <AppLink view="rfqs" navigate={navigate}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            All RFQs
          </AppLink>
        </Button>
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="flex items-center gap-3">
          <Badge value={row.status} />
          <span className="text-sm text-[var(--text-secondary)]">
            Deadline {displayDate(row.deadline)}
          </span>
        </div>
        <RfqActions
          row={row}
          busy={busy}
          can={can}
          role={role}
          onAct={onAct}
          onQuote={onQuote}
          onEvaluate={onEvaluate}
        />
      </div>
      <ControlPassportPanel
        organisationId={organisationId}
        checkpoint={row.status === "awarded" ? "award" : "rfq"}
        requestNo={row.requisitions?.request_no}
        viewerRole={role}
        capabilities={{
          manage: can("manage"),
          approve: can("approve"),
          procure: can("procure"),
          finance: can("finance"),
          receive: can("receive"),
        }}
        onNavigate={navigate}
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel>
          <p className="eyebrow">SOURCE REQUIREMENT</p>
          <h2 className="mt-1 font-serif text-xl">Approved request</h2>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Request</dt>
              <dd className="mt-1 font-medium">
                <AppLink
                  view="requisitions"
                  recordId={row.requisition_id}
                  navigate={navigate}
                >
                  {row.requisitions?.request_no || "Open requisition"}
                </AppLink>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Approved estimate</dt>
              <dd className="mt-1 font-medium tabular-nums">
                {money(row.requisitions?.estimated_value)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Created</dt>
              <dd className="mt-1 font-medium">{displayDate(row.created_at, true)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Current state</dt>
              <dd className="mt-1 font-medium">{row.status}</dd>
            </div>
          </dl>
        </Panel>
        <Panel>
          <p className="eyebrow">COMMERCIAL EVIDENCE</p>
          <h2 className="mt-1 font-serif text-xl">Suppliers and quotations</h2>
          {loading && <p className="mt-4 text-sm text-[var(--text-secondary)]">Loading tenant-scoped evidence…</p>}
          {error && <p className="mt-4 text-sm text-[var(--error)]">{error}</p>}
          {!loading && !error && data && (
            <div className="mt-4 grid gap-3">
              <p className="text-sm text-[var(--text-secondary)]">
                {data.vendors.length} invited supplier{data.vendors.length === 1 ? "" : "s"} · {data.quotes.length} current quotation{data.quotes.length === 1 ? "" : "s"}
              </p>
              {data.quotes.map((quote) => (
                <div className="rounded-lg border border-[var(--border)] p-3" key={quote.id}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <strong className="text-sm">{quote.vendors?.name || quote.quote_no}</strong>
                      <p className="mt-1 text-xs text-[var(--text-secondary)]">
                        {quote.quote_no} · {quote.delivery_days} delivery days
                      </p>
                    </div>
                    <strong className="text-sm tabular-nums">{money(quote.total, quote.currency)}</strong>
                  </div>
                  <p className="mt-2 text-xs text-[var(--text-secondary)]">
                    Verification: {quote.verification_state || "not recorded"} · Comparison: {quote.comparison_state || "not recorded"}
                  </p>
                </div>
              ))}
              {!data.quotes.length && (
                <p className="text-sm text-[var(--text-secondary)]">No current quotation is recorded.</p>
              )}
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}

function RfqActions({
  row,
  busy,
  can,
  role,
  onAct,
  onQuote,
  onEvaluate,
  mobile = false,
}: {
  row: Rfq;
  busy: string;
  can: ViewProps["can"];
  role: AppRole;
  onAct: (id: string, action: "publish" | "close") => void;
  onQuote: (id: string) => void;
  onEvaluate: (id: string) => void;
  mobile?: boolean;
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${mobile ? "[&>button]:min-h-11 [&>button]:flex-1" : ""}`}>
      {can("procure") && row.status === "draft" && (
        <Button
          size="sm"
          disabled={busy === row.id}
          onClick={() => onAct(row.id, "publish")}
        >
          {busy === row.id ? "Publishing…" : "Publish"}
        </Button>
      )}
      {(can("procure") || ["approver", "auditor"].includes(role)) &&
        ["published", "closed"].includes(row.status) && (
          <Button size="sm" variant="outline" onClick={() => onQuote(row.id)}>
            {can("procure")
              ? "Record received quotation"
              : role === "approver"
                ? "Review quotation evidence"
                : "View quotation evidence"}
          </Button>
        )}
      {can("procure") && row.status === "published" && (
        <Button
          size="sm"
          disabled={busy === row.id}
          onClick={() => onAct(row.id, "close")}
        >
          {busy === row.id ? "Closing…" : "Close RFQ"}
        </Button>
      )}
      {(can("procure") || can("approve")) && row.status === "closed" && (
        <Button size="sm" onClick={() => onEvaluate(row.id)}>
          {can("procure") ? "Compare & award" : "Review & approve award"}
        </Button>
      )}
      {row.status === "awarded" && (
        <span className="self-center text-xs text-[var(--text-muted)]">PO created</span>
      )}
    </div>
  );
}

function RfqDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
}: DialogProps) {
  const { data: requisitions } = useData(
    () => listRequisitions(organisationId, "approved"),
    [organisationId, open],
  );
  const { data: vendors } = useData(
    () => listVendors(organisationId),
    [organisationId, open],
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createRfq(
        organisationId,
        String(form.get("requisition")),
        String(form.get("deadline")),
        selected,
      );
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="SOURCE" title="Create RFQ" />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <Label>
            Approved requisition
            <Select name="requisition" required>
              {requisitions?.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.request_no} — {row.title}
                </option>
              ))}
            </Select>
          </Label>
          <Label>
            Quote deadline
            <Input name="deadline" type="date" required />
          </Label>
          <div>
            <strong className="text-xs">Invite vendors</strong>
            <div className="mt-2 max-h-48 overflow-auto rounded-lg border border-[var(--border)]">
              {vendors
                ?.filter((vendor) => vendor.active)
                .map((vendor) => (
                  <label
                    key={vendor.id}
                    className="flex min-h-11 items-center gap-3 border-b border-[var(--border)] p-3 text-sm last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(vendor.id)}
                      onChange={(event) =>
                        setSelected((current) =>
                          event.target.checked
                            ? [...current, vendor.id]
                            : current.filter((id) => id !== vendor.id),
                        )
                      }
                    />
                    {vendor.name} · {vendor.email}
                  </label>
                ))}
            </div>
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:min-h-11">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Create draft RFQ
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** @deprecated Buyer-side capture now uses QuotationEvidenceDialog and the database blocks this direct path. */
export function LegacyDirectQuoteDialog({
  rfqId,
  onOpenChange,
  organisationId,
  onDone,
}: {
  rfqId?: string;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const { data } = useData(
    () =>
      rfqId ? rfqDetail(organisationId, rfqId) : Promise.resolve(undefined),
    [organisationId, rfqId],
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const existing = new Set(data?.quotes.map((quote) => quote.vendor_id));
  const lines = data?.rfq.requisitions.requisition_lines || [];
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!rfqId) return;
    const form = new FormData(event.currentTarget);
    const quoteLines = lines
      .filter((line: any) => form.get(`include-${line.id}`))
      .map((line: any) => ({
        requisition_line_id: line.id,
        quantity: Number(form.get(`qty-${line.id}`)),
        unit_price: Number(form.get(`price-${line.id}`)),
        complies: Boolean(form.get(`complies-${line.id}`)),
        remarks: String(form.get(`remarks-${line.id}`) || ""),
      }));
    setBusy(true);
    setError("");
    try {
      await actions.recordQuote(organisationId, {
        p_rfq_id: rfqId,
        p_vendor_id: form.get("vendor"),
        p_quote_no: form.get("quoteNo"),
        p_valid_until: form.get("validUntil"),
        p_delivery_days: Number(form.get("deliveryDays")),
        p_commercial_mode: form.get("mode"),
        p_lines: quoteLines,
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(rfqId)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="QUOTATION" title="Record vendor quote" />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Vendor
              <Select name="vendor">
                {data?.vendors
                  .filter((vendor) => !existing.has(vendor.id))
                  .map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
              </Select>
            </Label>
            <Label>
              Quote number
              <Input name="quoteNo" required />
            </Label>
            <Label>
              Valid until
              <Input name="validUntil" type="date" required />
            </Label>
            <Label>
              Delivery days
              <Input
                name="deliveryDays"
                type="number"
                min="0"
                defaultValue="7"
                required
              />
            </Label>
          </div>
          <Label>
            Commercial mode
            <Select name="mode">
              <option value="partial">Partial line award permitted</option>
              <option value="all_or_nothing">All lines or no award</option>
            </Select>
          </Label>
          <div className="grid gap-3 rounded-xl bg-slate-50 p-4">
            {lines.map((line: any) => (
              <div
                key={line.id}
                className="grid gap-2 border-b border-slate-200 pb-3 md:grid-cols-[auto_1.5fr_.5fr_.7fr_auto_1fr]"
              >
                <label className="flex items-center gap-2 text-xs">
                  <input
                    name={`include-${line.id}`}
                    type="checkbox"
                    defaultChecked
                  />
                  Include
                </label>
                <div>
                  <strong className="text-sm">{line.name}</strong>
                  <small className="block text-slate-500">
                    {line.specification}
                  </small>
                </div>
                <Input
                  aria-label="Quantity"
                  name={`qty-${line.id}`}
                  type="number"
                  defaultValue={line.quantity}
                  max={line.quantity}
                  min="0.01"
                  step="0.01"
                />
                <Input
                  aria-label="Unit price"
                  name={`price-${line.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  required
                />
                <label className="flex items-center gap-2 text-xs">
                  <input
                    name={`complies-${line.id}`}
                    type="checkbox"
                    defaultChecked
                  />
                  Meets spec
                </label>
                <Input
                  aria-label="Remarks"
                  name={`remarks-${line.id}`}
                  placeholder="Remarks"
                />
              </div>
            ))}
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              Record quotation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EvaluationDialog({
  rfqId,
  onOpenChange,
  organisationId,
  onDone,
  navigate,
}: {
  rfqId?: string;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
  navigate: (view: string, recordId?: string) => void;
}) {
  const { data } = useData(
    () =>
      rfqId ? rfqDetail(organisationId, rfqId) : Promise.resolve(undefined),
    [organisationId, rfqId],
  );
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [controlResult, setControlResult] = useState<Record<string, any>>();
  const requested = data?.rfq.requisitions.requisition_lines.length || 0;
  const quotes = (data?.quotes || []).filter(
    (quote) => quote.is_current !== false && quote.comparison_state !== "excluded",
  );
  const choice = selected || quotes[0]?.id || "";
  const lowestQuote = [...quotes].sort(
    (left, right) => Number(left.total_base ?? left.total) - Number(right.total_base ?? right.total),
  )[0];
  const selectedQuote = quotes.find((quote) => quote.id === choice);
  const isNonL1 = Boolean(
    selectedQuote &&
      lowestQuote &&
      Number(selectedQuote.total_base ?? selectedQuote.total) >
        Number(lowestQuote.total_base ?? lowestQuote.total),
  );
  useEffect(() => {
    let live = true;
    setControlResult(undefined);
    if (!rfqId || !choice) return () => { live = false; };
    evaluateAwardControls(organisationId, rfqId, choice)
      .then((result) => { if (live) setControlResult(result); })
      .catch(() => { if (live) setControlResult(undefined); });
    return () => { live = false; };
  }, [choice, organisationId, rfqId]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!rfqId) return;
    const reason = String(
      new FormData(event.currentTarget).get("override") || "",
    );
    setBusy(true);
    setError("");
    try {
      await actions.awardQuote(organisationId, rfqId, choice, reason);
      onOpenChange(false);
      onDone();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(rfqId)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="COMMERCIAL EVALUATION"
          title="Compare and award"
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="hidden md:block">
          <Table
            heads={["Select", "Vendor", "Provenance", "Offer", "Delivery", "Specification"]}
          >
            {quotes.map((quote) => {
              const compliant =
                quote.quote_lines?.filter((line) => line.complies).length || 0;
              const full =
                compliant === requested &&
                quote.quote_lines?.length === requested;
              return (
                <tr key={quote.id}>
                  <td>
                    <input
                      type="radio"
                      checked={choice === quote.id}
                      onChange={() => setSelected(quote.id)}
                    />
                  </td>
                  <td>
                    {quote.vendors?.name}
                    <small className="block text-slate-500">
                      {"★".repeat(quote.vendors?.rating || 0)}
                    </small>
                  </td>
                  <td>
                    <div className="flex max-w-44 flex-wrap gap-1">
                      {provenanceBadges(quote).map((badge) => (
                        <span key={badge.value} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-700">
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{money(quote.total)}</td>
                  <td>{quote.delivery_days} days</td>
                  <td>
                    <Badge value={full ? "approved" : "exception"} />
                    <small className="block text-slate-500">
                      {compliant}/{requested} compliant
                    </small>
                  </td>
                </tr>
              );
            })}
          </Table>
          </div>
          <div className="grid gap-3 md:hidden" role="radiogroup" aria-label="Quotation comparison">
            {quotes.map((quote) => {
              const compliant =
                quote.quote_lines?.filter((line) => line.complies).length || 0;
              const full =
                compliant === requested &&
                quote.quote_lines?.length === requested;
              const quoteIsL1 = quote.id === lowestQuote?.id;
              return (
                <label
                  key={quote.id}
                  className={`rounded-xl border p-4 ${choice === quote.id ? "border-[var(--action)] bg-[var(--action-soft)]" : "border-[var(--border)]"}`}
                >
                  <span className="flex items-start justify-between gap-3">
                    <span>
                      <input
                        className="mr-2"
                        type="radio"
                        name="selectedQuoteMobile"
                        checked={choice === quote.id}
                        onChange={() => setSelected(quote.id)}
                      />
                      <strong>{quote.vendors?.name}</strong>
                    </span>
                    <span className="text-right">
                      <strong className="block tabular-nums">{money(quote.total, quote.currency)}</strong>
                      <span className="text-xs text-[var(--text-secondary)]">{quoteIsL1 ? "L1 · lowest evaluated cost" : "Non-L1 option"}</span>
                    </span>
                  </span>
                  <span className="mt-3 grid grid-cols-2 gap-3 text-xs">
                    <span><strong className="block">Delivery</strong>{quote.delivery_days} days</span>
                    <span><strong className="block">Specification</strong>{compliant}/{requested} compliant · {full ? "complete" : "exception"}</span>
                  </span>
                  <span className="mt-3 flex flex-wrap gap-1">
                    {provenanceBadges(quote).map((badge) => (
                      <span key={badge.value} className="rounded-md border border-[var(--border)] bg-[var(--surface-secondary)] px-2 py-1 text-[10px] font-semibold">
                        {badge.label}
                      </span>
                    ))}
                  </span>
                </label>
              );
            })}
          </div>
          <Label>
            {isNonL1 ? "Required non-L1 selection justification" : "Policy exception justification (when applicable)"}
            <Textarea
              name="override"
              required={isNonL1}
              minLength={isNonL1 ? 10 : undefined}
              aria-describedby="award-justification-help"
            />
            <span id="award-justification-help" className="text-xs text-[var(--text-secondary)]">
              {isNonL1
                ? `The selected offer is ${money(
                    Number(selectedQuote?.total_base ?? selectedQuote?.total ?? 0) -
                      Number(lowestQuote?.total_base ?? lowestQuote?.total ?? 0),
                  )} above L1. Record the permitted commercial or technical reason; an active policy may also require approval or a waiver.`
                : "Leave blank for an ordinary compliant L1 award. Any permitted exception is retained with the award evidence."}
            </span>
          </Label>
          {controlResult && controlResult.enforcement_mode !== "disabled" && (
            <div
              className={`rounded-xl border p-4 ${controlResult.would_block ? "border-[var(--error-border)] bg-[var(--error-soft)]" : isNonL1 ? "border-[var(--warning-border)] bg-[var(--warning-soft)]" : "border-[var(--success-border)] bg-[var(--success-soft)]"}`}
              role={controlResult.would_block ? "alert" : "status"}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <strong>Internal-control preflight</strong>
                <Badge value={controlResult.result || "clear"} />
              </div>
              <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="font-semibold">Why</dt>
                  <dd className="mt-1">
                    {controlResult.would_block
                      ? "The active organisation controls are not satisfied for this award."
                      : isNonL1
                        ? "A supplier other than L1 is selected and the commercial difference requires an attributable reason."
                        : "The selected L1 offer satisfies the current preflight inputs."}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold">Policy</dt>
                  <dd className="mt-1">
                    Active evaluated control set · {controlResult.required_quotation_count || 0} quotation(s) · {controlResult.required_approval_count || 0} approval(s)
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold">Next</dt>
                  <dd className="mt-1">
                    {controlResult.would_block
                      ? "Add the missing quotation or approval, or request the policy-permitted waiver."
                      : isNonL1
                        ? "Record the permitted justification and supporting evidence before award."
                        : "Proceed with the governed award."}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold">Resolver</dt>
                  <dd className="mt-1">
                    {controlResult.would_block ? "Procurement and the policy-named approver" : "Procurement"}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-sm font-semibold">
                Selected {money(controlResult.selected_total_base)} · L1 {money(controlResult.l1_total_base)} · difference {money(controlResult.variance_from_l1)}
              </p>
              {(controlResult.would_block || isNonL1) && (
                <Button
                  className="mt-3 min-h-11"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false);
                    navigate("internal-controls", rfqId);
                  }}
                >
                  Review controls or request waiver
                </Button>
              )}
            </div>
          )}
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !choice}>
              Award & create PO
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
